import { usePageTitle } from "@/hooks/usePageTitle";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const LOGO = "https://customer-assets-jai6qajn.emergentagent.net/job_inbody-powered/artifacts/cgjm5igj_FB%20Cover%20logo.png";

function GoogleButton() {
  const handle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button onClick={handle} data-testid="google-login-btn" type="button"
      className="w-full flex items-center justify-center gap-3 border border-[#E7E5E4] rounded-lg py-3 font-medium text-navy hover:bg-[#F5F5F4] transition-colors">
      <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" />
      Continue with Google
    </button>
  );
}

export default function Login() {
  usePageTitle("Log In");
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back!");
      const dest = u.role === "nutrition_therapist" ? "/dashboard" : u.role === "admin" ? "/admin" : "/portal";
      navigate(dest);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-navy">
        <div className="brand-blob bg-teal" style={{ width: 380, height: 380, top: 80, left: 60 }} />
        <div className="relative h-full flex flex-col justify-center px-16 text-white">
          <img src={LOGO} alt="MBBC" className="h-16 w-16 object-contain rounded-xl bg-white p-1.5" />
          <h1 className="mt-8 font-heading text-4xl tracking-tight leading-tight">Your data-driven<br />nutrition journey awaits.</h1>
          <p className="mt-4 text-white/70 max-w-sm">Log in to access your member content, track BIA progress, or manage your therapist dashboard.</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex justify-center mb-8"><img src={LOGO} alt="MBBC" className="h-14 w-14 object-contain" /></Link>
          <h2 className="font-heading text-3xl text-navy">Welcome back</h2>
          <p className="mt-2 text-[#57534E] text-sm">Don't have an account? <Link to="/join" className="text-teal font-medium">Join the club</Link></p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-sm font-medium text-navy">Email</label>
              <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full bg-white border border-[#E7E5E4] rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal transition-all" placeholder="you@example.com" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-navy">Password</label>
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-teal font-medium hover:underline">Forgot password?</Link>
              </div>
              <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full bg-white border border-[#E7E5E4] rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal transition-all" placeholder="••••••••" />
            </div>
            {err && <p data-testid="login-error" className="text-sm text-red-600">{err}</p>}
            <button data-testid="login-submit" disabled={busy} type="submit"
              className="w-full bg-navy text-white hover:bg-navy-dark transition-colors rounded-lg py-3 font-medium flex items-center justify-center gap-2 disabled:opacity-60">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Log in
            </button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs text-[#A8A29E]">
            <div className="h-px flex-1 bg-[#E7E5E4]" /> OR <div className="h-px flex-1 bg-[#E7E5E4]" />
          </div>
          <GoogleButton />
        </div>
      </div>
    </div>
  );
}
